import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/router'
import type { FormEvent, ReactElement } from 'react'
import { useForm } from 'react-hook-form'

import { FooterActions } from '@/modules/ouvidoria/FooterActions'
import { OuvidoriaContactInfo } from '@/modules/ouvidoria/components/OuvidoriaContactInfo/index'
import { OuvidoriaLayout } from '@/modules/ouvidoria/layout/index'
import { useValidateOuvidoriaTicket } from '@/modules/ouvidoria/operations/useValidateOuvidoriaTicket'
import {
  type PIDFormData,
  pidDataFormSchema,
} from '@/modules/ouvidoria/pages/identificacao/pidFormSchema'
import { Container } from '@/modules/ouvidoria/pages/identificacao/styles'
import { useOuvidoriaState } from '@/modules/ouvidoria/state'
import type { NextPageWithLayout } from '@/shared/page'
import { FormContainer } from '@/shared/ui/Form/FormContainer'
import { FormDescription } from '@/shared/ui/Form/FormDescription'
import { TextField } from '@/shared/ui/Form/TextField/index'
import { Link } from '@/shared/ui/Link'
import { Text, Title } from '@/shared/ui/Typography'
import { onlyNumbers } from '@/shared/validation/index'
import { maskCPF } from '@/shared/validation/masks'

const Identification: NextPageWithLayout = () => {
  const router = useRouter()
  const {
    formState,
    setFormState,
    setCancelModalOpen,
    cancelModalOpen,
    setCurrentStep,
  } = useOuvidoriaState()

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<PIDFormData>({
    resolver: zodResolver(pidDataFormSchema),
    defaultValues: {
      cpf: maskCPF(formState.cpf),
      ticket: formState.ticket,
    },
  })

  function handleGoBack(event: FormEvent) {
    event.preventDefault()
    router.push('/ouvidoria/personal-data')
    setCurrentStep((prev) => prev - 1)
  }

  async function handleNextStep({ cpf, ticket }: PIDFormData) {
    const { isTicketValid } = await useValidateOuvidoriaTicket(ticket)
    setFormState((prevState) => ({
      ...prevState,
      cpf,
      ticket,
      isTicketValid,
    }))

    if (!isTicketValid) {
      return router.push('/ouvidoria/sinto-muito')
    }

    setCurrentStep((prev) => prev + 1)
    router.push('/ouvidoria/mensagem')
  }

  return (
    <Container>
      <form onSubmit={handleSubmit(handleNextStep)}>
        <div>
          <FormDescription>
            <Title as="h1" size="headline7">
              Ouvidoria da Flash | Atendimento para casos especiais
            </Title>
            <Text size="body3">
              A Ouvidoria da Flash avalia casos especiais de atendimentos à
              clientes, sejam administradores ou colaboradores, que precisam de
              devolutiva mais detalhada.
            </Text>
            <OuvidoriaContactInfo />
          </FormDescription>

          <FormContainer id="personal-data-form">
            <fieldset>
              <TextField
                type="text"
                autoComplete="on"
                label="CPF"
                error={errors.cpf?.message}
                placeholder="000.000.000-00"
                {...register('cpf', {
                  onChange(event) {
                    const { value } = event.target
                    event.target.value = maskCPF(value)
                  },
                })}
              />

              <TextField
                type="text"
                label="Ticket de atendimento"
                autoComplete="on"
                error={errors.ticket?.message}
                placeholder="0000000000"
                {...register('ticket', {
                  onChange(event) {
                    const { value } = event.target
                    event.target.value = onlyNumbers(value)
                  },
                })}
              />
            </fieldset>
          </FormContainer>
        </div>

        <FooterActions
          cancelModalOpen={cancelModalOpen}
          onCancelModalOpen={() => setCancelModalOpen((prev) => !prev)}
          onHandleGoBack={handleGoBack}
          primaryButtonDisabled={isSubmitting}
          loading={isSubmitting}
        />
      </form>
    </Container>
  )
}

Identification.getLayout = function getLayout(page: ReactElement) {
  return <OuvidoriaLayout>{page}</OuvidoriaLayout>
}

export default Identification
